import { useState } from 'react';
import { useIntl } from 'react-intl';
import { CreativesModal } from 'sonata-platform-components';
import { ButtonUI, ContextualMenu } from 'sonata-react-components';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { DevicePlatformEnum } from 'sonata-static-storage/lib/schemas/campaign/targeting/devicePlatform.schema';

import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';

import type { AspectRatioByAdType, MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { ScreenInterface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

export function AddCreatives({
  aspectRatiosByAdType,
  className = '',
  countries,
  devicePlatforms,
  entityFamily,
  entityId,
  entityType,
  onSave,
  onVenuesChange,
  screens,
  user,
  userRoles,
  venues
}: Readonly<{
  aspectRatiosByAdType: AspectRatioByAdType[];
  className?: string;
  countries: string[];
  devicePlatforms: DevicePlatformEnum[];
  entityFamily: CampaignFamilyEnum;
  entityId: string;
  entityType: Omit<MultiFlightEntityType, 'campaignPlan'>;
  onSave: (data: any) => void;
  onVenuesChange: (venues: Venue<string>[]) => void;
  screens: ScreenInterface[];
  user: any;
  userRoles: any[];
  venues: Venue<string>[];
}>) {
  const { formatMessage } = useIntl();
  const [mode, setMode] = useState('');
  const currencyInfo = useGlobalStore(getBaseCurrency);

  return (
    <>
      <ContextualMenu id={'addCreatives-menu'} placement="bottom">
        <ContextualMenu.Trigger>
          <ButtonUI
            className={className}
            icon="fa fa-regular fa-plus"
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Add Creatives',
              id: 'creatives.adCreatives.emptyState.button'
            })}
            type="primary"
          />
        </ContextualMenu.Trigger>
        <ContextualMenu.Item
          icon="fas fa-image"
          onClick={() => setMode('single')}
          text={formatMessage({
            defaultMessage: 'Ad Creatives',
            id: 'creatives.adCreatives.emptyState.popover.options.adCreatives'
          })}
        />
        {entityFamily !== CampaignFamilyEnum.dooh && (
          <ContextualMenu.Item
            icon="fas fa-redo"
            onClick={() => setMode('redirect')}
            text={formatMessage({
              defaultMessage: 'Redirect Ad Creative',
              id: 'creatives.adCreatives.emptyState.popover.options.redirectAdCreative'
            })}
          />
        )}
        <ContextualMenu.Item
          icon="fas fa-arrow-up-to-line"
          onClick={() => setMode('bulk')}
          text={formatMessage({
            defaultMessage: 'Bulk Upload',
            id: 'creatives.adCreatives.emptyState.popover.options.bulkUpload'
          })}
        />
      </ContextualMenu>
      {mode ? (
        <CreativesModal
          aspectRatiosByAdType={aspectRatiosByAdType}
          countries={countries}
          currencyInfo={currencyInfo}
          devicePlatforms={devicePlatforms}
          entityFamily={entityFamily}
          entityId={entityId}
          entityType={entityType}
          isModalOpened={!!mode}
          mode={mode}
          onModalClose={() => setMode('')}
          onSave={onSave}
          onVenuesChange={onVenuesChange}
          prefixId="add-creatives"
          screens={screens}
          user={{
            ...user,
            _id: user.id || user._id
          }}
          userRoles={userRoles}
          venues={venues}
        />
      ) : null}
    </>
  );
}
